import React from 'react';
import { OrganisationMapMode } from 'enums/organisationMapEnums';
import { Organization } from 'models/CompanySetup/organization.model';
import { ConnectionLineType, Edge, Node } from 'reactflow';
import { MetaData } from 'models/MetaData/metaData.model';
import { OrganisationMapPayload } from 'models/CompanySetup/organisationPayload.model';
import OrganisationEditNode from '../OrganisationMapNodes/OrganisationEditNode';
import OrganisationViewNode from '../OrganisationMapNodes/OrganisationViewNode';

const ORGANIZATION_MAP_NODE = 'organisationMapNode';
const position = { x: 0, y: 0 }; //Initial Position before calculating to form the tree
const edgeType = ConnectionLineType.SmoothStep;
const edgeStyle = {
  stroke: '#D4DBDB', //Default Edge Color
  strokeWidth: 1,
};
interface ModifiedData {
  modifiedData: Organization[];
  mainParentIndex: number;
}

const modifyOrganisationData = (data: Organization[]): ModifiedData => {
  const idToIndexMap = new Map<string, number>();
  data.forEach((org, index) => {
    idToIndexMap.set(org.name ?? '', index);
  });
  let mainParentIndex = -1;
  const modifiedData = data.map((org, index) => {
    const parentIndex = org?.organisationYearWise?.parentName
      ? idToIndexMap.get(org.organisationYearWise.parentName) ?? -1
      : -1;
    if (!org?.organisationYearWise?.parentName) mainParentIndex = index;
    return {
      ...org,
      organisationYearWise: {
        ...org.organisationYearWise,
        parentIndex,
      },
    };
  });
  return { modifiedData, mainParentIndex };
};

export interface OrganizationNodeType extends Node {
  organizationId: string;
  hidden?: boolean;
  data: {
    label: string;
    hasSource: boolean;
    hasTarget: boolean;
    nodeHeight: number;
  };
  isFocusNode: boolean;
}

export const generateNodesAndEdges = (props: {
  data: Organization[];
  mode: OrganisationMapMode;
  organisationBoundary?: string;
  organisationListsMeta?: MetaData[];
  year?: number;
  validateOrganisationStatus?: (
    params: { year?: number },
    id: number
  ) => Promise<boolean | undefined>;
  setOrganisationMapData?: React.Dispatch<
    React.SetStateAction<OrganisationMapPayload>
  >;
  deleteCallBack?: () => Promise<OrganisationMapPayload>;
  collapsedNodes?: Set<string>;
  onToggleCollapse?: (nodeId: string) => void;
  canEdit?: boolean;
}) => {
  const {
    data,
    mode,
    organisationBoundary,
    organisationListsMeta,
    year,
    validateOrganisationStatus,
    setOrganisationMapData,
    deleteCallBack,
    collapsedNodes,
    onToggleCollapse,
    canEdit = true,
  } = props;
  //Organisation data is modified to identify parentNode's Index for edges generation
  const { modifiedData, mainParentIndex } = modifyOrganisationData(data);
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // First pass: build edges to identify which nodes have children
  const nodeIdToChildrenCount = new Map<string, number>();
  modifiedData?.forEach((organisation: Organization, index) => {
    const parentIndex = organisation.organisationYearWise?.parentIndex;
    if (parentIndex !== -1) {
      const parentNodeId = `${ORGANIZATION_MAP_NODE} ${parentIndex}`;
      nodeIdToChildrenCount.set(
        parentNodeId,
        (nodeIdToChildrenCount.get(parentNodeId) || 0) + 1
      );
    }
  });

  modifiedData?.forEach((organisation: Organization, index) => {
    const parentIndex = organisation.organisationYearWise?.parentIndex;
    const nodeId = `${ORGANIZATION_MAP_NODE} ${index}`;
    const hasChildren = (nodeIdToChildrenCount.get(nodeId) || 0) > 0;
    const nodeLabel =
      mode === OrganisationMapMode.EDIT ? (
        <OrganisationEditNode
          organisation={organisation}
          index={index}
          organisationList={data}
          organisationListMeta={organisationListsMeta}
          year={year}
          validateOrganisationStatus={validateOrganisationStatus}
          setOrganisationMapData={setOrganisationMapData}
          canEdit={canEdit}
        />
      ) : mode === OrganisationMapMode.VIEW ? (
        <OrganisationViewNode
          organisation={organisation}
          shareType={
            organisation.organisationYearWise?.parentName
              ? organisationBoundary
              : undefined
          }
          deleteCallBack={deleteCallBack}
          nodeId={nodeId}
          isCollapsed={collapsedNodes?.has(nodeId)}
          onToggleCollapse={hasChildren ? onToggleCollapse : undefined}
        />
      ) : (
        ''
      );

    //Organisation's Node Data
    const node = {
      id: `${ORGANIZATION_MAP_NODE} ${index}`,
      hidden: !organisation.organisationYearWise?.isActive,
      data: {
        label: nodeLabel,
        hasSource: true,
        hasTarget: true,
        nodeHeight:
          mode === OrganisationMapMode.VIEW &&
          !organisation.organisationYearWise?.isParent
            ? 90
            : 60,
      },
      position,
      organizationId: organisation.id ?? '',
      isFocusNode: parentIndex == mainParentIndex,
    };
    nodes.push(node);

    //Generating edge if organisation has a parentIndex
    if (parentIndex !== -1) {
      const source = `${ORGANIZATION_MAP_NODE} ${organisation?.organisationYearWise?.parentIndex}`;
      const target = `${ORGANIZATION_MAP_NODE} ${index}`;
      const edge = {
        id: `edge-${source}-${target}`,
        source: source,
        target: target,
        type: edgeType,
        style: edgeStyle,
      };
      edges.push(edge);
    }
  });
  return { nodes, edges };
};
